import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, Mail } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import BrandLockup from "../components/BrandLockup";
import ReviewsSlideshow from "../components/ReviewsSlideshow";
import HomeGuides from "../components/HomeGuides";
import InquiryForm from "../components/InquiryForm";
import { BRAND, IMAGES, SERVICES, STATS } from "../data/content";
import { useT, useLang } from "../i18n/LanguageContext";
import { BRAND_DESCRIPTION_IT, localizeStats, localizeManifesto, localizeServices } from "../i18n/content_it";

const MANIFESTO = [
  { n: "01", t: "Understated luxury", d: "No badges, no bravado. Just a spotless Mercedes V-Class, a chauffeur of impeccable discretion, and the confidence that everything is handled." },
  { n: "02", t: "Tuscany, intimately", d: "Based in Lucca and Pisa, we know the back roads, the golden hours and the tables worth booking. You travel as a guest of the region, never a tourist." },
  { n: "03", t: "Time you keep", d: "From cruise terminals at Livorno and La Spezia to private yachts at Viareggio and Carrara — we build every hour around your schedule, and give it back to you." },
];

// Bento column spans by card position; anything past the first two spans 4 cols.
const SERVICE_SPANS = ["md:col-span-7", "md:col-span-5"];

export default function Home() {
  const t = useT();
  const { lang } = useLang();
  const stats = localizeStats(STATS, lang);
  const manifesto = localizeManifesto(MANIFESTO, lang);
  const services = localizeServices(SERVICES, lang);
  const brandDescription = lang === "it" ? BRAND_DESCRIPTION_IT : BRAND.description;

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);

  return (
    <div>
      <Seo
        title={t("seo.home.title")}
        description={t("seo.home.desc")}
        locale={lang === "it" ? "it_IT" : "en_US"}
        path="/"
      />

      {/* HERO */}
      <section ref={heroRef} className="relative h-screen w-full overflow-hidden flex flex-col items-center justify-center text-center px-6 md:px-12" data-testid="hero">
        <motion.div className="absolute inset-0 -z-10" style={{ y: imgY }}>
          <img src={IMAGES.heroMercedes} alt="Black Mercedes V-Class chauffeur car in Tuscany" className="w-full h-[125%] object-cover" />
        </motion.div>
        <motion.div className="absolute inset-0 -z-10 bg-black" style={{ opacity: overlayOpacity }} />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/40" />

        <div className="max-w-4xl w-full mx-auto flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.33, 1, 0.68, 1] }}
            className="drop-shadow-[0_4px_30px_rgba(0,0,0,0.7)]"
          >
            <BrandLockup size="hero" data-testid="hero-logo" />
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }} className="overline mt-10">
            {t("hero.overline")}
          </motion.p>
          <motion.a
            href="#contact"
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="group mt-8 flex items-center gap-3 bg-[#C5A059] text-[#0A0A0A] px-8 py-4 uppercase tracking-[0.2em] text-sm hover:bg-[#D4AF37] transition-colors"
          >
            {t("hero.cta")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </motion.a>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y border-white/10 bg-[#0A0A0A]" data-testid="stats-strip">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="px-6 py-10 md:py-14 text-center">
                <div className="font-serif text-4xl md:text-6xl text-[#C5A059] font-light">{s.value}</div>
                <div className="text-white/50 text-xs md:text-sm mt-2 tracking-wide">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="px-6 md:px-12 py-24 md:py-36 max-w-[1600px] mx-auto" data-testid="manifesto">
        <Reveal>
          <p className="overline mb-4">{t("philosophy.overline")}</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light max-w-3xl leading-tight">
            {t("philosophy.headline")}
          </h2>
          <p className="text-white/70 mt-8 max-w-3xl leading-relaxed text-lg">{brandDescription}</p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-20">
          {manifesto.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.1}>
              <div className="border-t border-white/15 pt-6">
                <span className="font-serif text-5xl text-[#C5A059]/80">{m.n}</span>
                <h3 className="font-serif text-2xl mt-4 mb-3">{m.t}</h3>
                <p className="text-white/60 leading-relaxed text-sm">{m.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="px-6 md:px-12 pb-24 md:pb-32 max-w-[1600px] mx-auto" data-testid="services-overview">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <p className="overline mb-4">{t("servicesOverview.overline")}</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight font-light">{t("servicesOverview.title")}</h2>
            </div>
            <Link to="/services" data-testid="services-link" className="group flex items-center gap-2 text-[#C5A059] text-sm uppercase tracking-[0.2em]">
              {t("servicesOverview.all")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {services.map((s, i) => {
            const span = SERVICE_SPANS[i] || "md:col-span-4";
            return (
              <Reveal key={s.id} delay={(i % 3) * 0.08} className={span}>
                <Link to="/services" className="group block relative h-72 md:h-80 overflow-hidden">
                  <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-7">
                    <h3 className="font-serif text-2xl md:text-3xl text-[#F5F5F0]">{s.title}</h3>
                    <p className="text-white/60 text-sm mt-2 max-w-md">{s.short}</p>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FIXED PARALLAX TEXT — real photo: V-Class alongside the cruise ship */}
      <HomeGuides />

      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden" data-testid="fixed-bg-section">
        <div className="absolute inset-0 bg-fixed-hero" style={{ backgroundImage: `url(${IMAGES.shoreExcursion})` }} />
        <div className="absolute inset-0 bg-black/60" />
        <Reveal className="relative z-10 text-center px-6">
          <p className="overline mb-6">{t("fixed.overline")}</p>
          <p className="font-serif text-3xl sm:text-5xl lg:text-6xl font-light leading-tight max-w-4xl mx-auto text-[#F5F5F0]">
            {t("fixed.headline")}
          </p>
          <p className="text-white/70 mt-8 max-w-xl mx-auto text-sm leading-relaxed">
            {t("fixed.sub")}
          </p>
        </Reveal>
      </section>

      <ReviewsSlideshow />

      {/* CONTACT */}
      <section id="contact" className="px-6 md:px-12 py-24 md:py-32 max-w-[1600px] mx-auto scroll-mt-20" data-testid="contact-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="overline mb-4">{t("contact.overline")}</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight font-light leading-tight">{t("contact.title")}</h2>
              <p className="text-white/60 mt-6 leading-relaxed text-sm">
                {t("contact.text")}
              </p>
              <div className="mt-10 space-y-4">
                <a href={BRAND.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-3 bg-[#25D366] text-white px-6 py-3 rounded-full hover:bg-[#128C7E] transition-colors w-fit">
                  <MessageCircle size={18} /> {BRAND.whatsapp}
                </a>
                <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="flex items-center gap-3 text-white/70 hover:text-[#C5A059] transition-colors text-sm">
                  <Mail size={16} /> {BRAND.email}
                </a>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <InquiryForm />
            </Reveal>
          </div>
        </div>
      </section>

      {/* AI IMAGERY DISCLAIMER */}
      <section className="border-t border-white/10 px-6 md:px-12 py-10 max-w-[1600px] mx-auto" data-testid="ai-imagery-disclaimer">
        <p className="text-white/40 text-xs leading-relaxed max-w-3xl">
          {lang === "it"
            ? "Alcune immagini presenti su questo sito sono state generate o elaborate con l'ausilio dell'intelligenza artificiale e hanno finalità puramente illustrative. Potrebbero non rappresentare veicoli, luoghi o dettagli reali del servizio."
            : "Some images on this website have been generated or enhanced using artificial intelligence and are intended for illustrative purposes only. They may not depict the actual vehicles, locations or service details."}
        </p>
      </section>
    </div>
  );
}
